/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { CellError, ErrorType } from '../../Cell';
import { FormulaCellVertex, MatrixVertex } from '../../DependencyGraph';
import { ErrorMessage } from '../../error-message';
import { AstNodeType } from '../../parser';
import { EmptyValue, isExtendedNumber } from '../InterpreterValue';
import { ArgumentTypes, FunctionPlugin } from './FunctionPlugin';
/**
 * Interpreter plugin containing information functions
 */
export class InformationPlugin extends FunctionPlugin {
    /**
     * Corresponds to ISBINARY(value)
     *
     * Returns true if provided value is a valid binary number
     *
     * @param ast
     * @param formulaAddress
     */
    isbinary(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISBINARY'), (arg) => /^[01]{1,10}$/.test(arg));
    }
    /**
     * Corresponds to ISERR(value)
     *
     * Returns true if provided value is an error except #N/A!
     *
     * @param ast
     * @param formulaAddress
     */
    iserr(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISERR'), (arg) => (arg instanceof CellError && arg.type !== ErrorType.NA));
    }
    /**
     * Corresponds to ISERROR(value)
     *
     * Checks whether provided value is an error
     *
     * @param ast
     * @param formulaAddress
     */
    iserror(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISERROR'), (arg) => (arg instanceof CellError));
    }
    /**
     * Corresponds to ISFORMULA(value)
     *
     * Checks whether referenced cell is a formula
     *
     * @param ast
     * @param formulaAddress
     */
    isformula(ast, formulaAddress) {
        return this.runFunctionWithReferenceArgument(ast.args, formulaAddress, this.metadata('ISFORMULA'), () => new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber), (reference) => {
            const vertex = this.dependencyGraph.addressMapping.getCell(reference);
            return vertex instanceof FormulaCellVertex || (vertex instanceof MatrixVertex && vertex.isFormula());
        });
    }
    /**
     * Corresponds to ISBLANK(value)
     *
     * Checks whether provided cell reference is empty
     *
     * @param ast
     * @param formulaAddress
     */
    isblank(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISBLANK'), (arg) => (arg === EmptyValue));
    }
    /**
     * Corresponds to ISNA(value)
     *
     * Returns true if provided value is #N/A! error
     *
     * @param ast
     * @param formulaAddress
     */
    isna(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISNA'), (arg) => (arg instanceof CellError && arg.type == ErrorType.NA));
    }
    /**
     * Corresponds to ISNUMBER(value)
     *
     * Checks whether provided cell reference is a number
     *
     * @param ast
     * @param formulaAddress
     */
    isnumber(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISNUMBER'), isExtendedNumber);
    }
    /**
     * Corresponds to ISLOGICAL(value)
     *
     * Checks whether provided cell reference is of logical type
     *
     * @param ast
     * @param formulaAddress
     */
    islogical(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISLOGICAL'), (arg) => (typeof arg === 'boolean'));
    }
    /**
     * Corresponds to ISREF(value)
     *
     * Returns true if provided value is #REF! error
     *
     * @param ast
     * @param formulaAddress
     */
    isref(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISREF'), (arg) => (arg instanceof CellError && (arg.type == ErrorType.REF || arg.type == ErrorType.CYCLE)));
    }
    /**
     * Corresponds to ISTEXT(value)
     *
     * Checks whether provided cell reference is of logical type
     *
     * @param ast
     * @param formulaAddress
     */
    istext(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISTEXT'), (arg) => (typeof arg === 'string'));
    }
    /**
     * Corresponds to ISNONTEXT(value)
     *
     * Checks whether provided cell reference is of logical type
     *
     * @param ast
     * @param formulaAddress
     */
    isnontext(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('ISNONTEXT'), (arg) => !(typeof arg === 'string'));
    }
    /**
     * Corresponds to COLUMN(reference)
     *
     * Returns column number of a reference or a formula cell if reference not provided
     *
     * @param ast
     * @param formulaAddress
     */
    column(ast, formulaAddress) {
        return this.runFunctionWithReferenceArgument(ast.args, formulaAddress, this.metadata('COLUMN'), () => formulaAddress.col + 1, (reference) => reference.col + 1);
    }
    /**
     * Corresponds to COLUMNS(range)
     *
     * Returns number of columns in provided range of cells
     *
     * @param ast
     * @param formulaAddress
     */
    columns(ast, formulaAddress) {
        if (ast.args.length !== 1) {
            return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
        }
        if (ast.args.some((astIt) => astIt.type === AstNodeType.EMPTY)) {
            return new CellError(ErrorType.NUM, ErrorMessage.EmptyArg);
        }
        const rangeAst = ast.args[0];
        if (rangeAst.type === AstNodeType.CELL_RANGE) {
            return rangeAst.end.col - rangeAst.start.col + 1;
        }
        else {
            return new CellError(ErrorType.VALUE, ErrorMessage.CellRangeExpected);
        }
    }
    /**
     * Corresponds to ROW(reference)
     *
     * Returns row number of a reference or a formula cell if reference not provided
     *
     * @param ast
     * @param formulaAddress
     */
    row(ast, formulaAddress) {
        return this.runFunctionWithReferenceArgument(ast.args, formulaAddress, this.metadata('ROW'), () => formulaAddress.row + 1, (reference) => reference.row + 1);
    }
    /**
     * Corresponds to ROWS(range)
     *
     * Returns number of rows in provided range of cells
     *
     * @param ast
     * @param formulaAddress
     */
    rows(ast, formulaAddress) {
        if (ast.args.length !== 1) {
            return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
        }
        if (ast.args.some((astIt) => astIt.type === AstNodeType.EMPTY)) {
            return new CellError(ErrorType.NUM, ErrorMessage.EmptyArg);
        }
        const rangeAst = ast.args[0];
        if (rangeAst.type === AstNodeType.CELL_RANGE) {
            return rangeAst.end.row - rangeAst.start.row + 1;
        }
        else {
            return new CellError(ErrorType.VALUE, ErrorMessage.CellRangeExpected);
        }
    }
    /**
     * Corresponds to INDEX(range;)
     *
     * Returns number of rows in provided range of cells
     *
     * @param ast
     * @param formulaAddress
     */
    index(ast, formulaAddress) {
        return this.runFunction(ast.args, formulaAddress, this.metadata('INDEX'), (rangeValue, row, col) => {
            var _a;
            if (col < 1 || row < 1) {
                return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
            }
            if (col > rangeValue.width() || row > rangeValue.height()) {
                return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
            }
            const range = rangeValue.range();
            if (range === undefined) {
                return (_a = rangeValue.topLeftCornerValue()) !== null && _a !== void 0 ? _a : new CellError(ErrorType.VALUE, ErrorMessage.CellRangeExpected);
            }
            const address = range.getAddress(col - 1, row - 1);
            return this.dependencyGraph.getScalarValue(address);
        });
    }
    /**
     * Corresponds to NA()
     *
     * Returns #N/A!
     *
     * @param _ast
     * @param _formulaAddress
     */
    na(_ast, _formulaAddress) {
        return new CellError(ErrorType.NA);
    }
    /**
     * Corresponds to SHEET(value)
     *
     * Returns sheet number of a given value or a formula sheet number if no argument is provided
     *
     * @param ast
     * @param formulaAddress
     * */
    sheet(ast, formulaAddress) {
        return this.runFunctionWithReferenceArgument(ast.args, formulaAddress, { parameters: [{ argumentType: ArgumentTypes.STRING }] }, () => formulaAddress.sheet + 1, (reference) => reference.sheet + 1, (value) => {
            const sheetNumber = this.dependencyGraph.sheetMapping.get(value);
            if (sheetNumber !== undefined) {
                return sheetNumber + 1;
            }
            else {
                return new CellError(ErrorType.NA, ErrorMessage.SheetRef);
            }
        });
    }
    /**
     * Corresponds to SHEETS(value)
     *
     * Returns number of sheet of a given reference or number of all sheets in workbook when no argument is provided.
     * It returns always 1 for a valid reference as 3D references are not supported.
     *
     * @param ast
     * @param formulaAddress
     * */
    sheets(ast, formulaAddress) {
        return this.runFunctionWithReferenceArgument(ast.args, formulaAddress, { parameters: [{ argumentType: ArgumentTypes.STRING }] }, () => this.dependencyGraph.sheetMapping.numberOfSheets(), // return number of sheets if no argument
        () => 1, // return 1 for valid reference
        () => new CellError(ErrorType.VALUE, ErrorMessage.CellRefExpected) // error otherwise
        );
    }
}
InformationPlugin.implementedFunctions = {
    'COLUMN': {
        method: 'column',
        parameters: [
            { argumentType: ArgumentTypes.NOERROR, optional: true }
        ],
        isDependentOnSheetStructureChange: true,
        doesNotNeedArgumentsToBeComputed: true,
    },
    'COLUMNS': {
        method: 'columns',
        isDependentOnSheetStructureChange: true,
        doesNotNeedArgumentsToBeComputed: true,
    },
    'ISBINARY': {
        method: 'isbinary',
        parameters: [
            { argumentType: ArgumentTypes.STRING }
        ]
    },
    'ISERR': {
        method: 'iserr',
        parameters: [
            { argumentType: ArgumentTypes.SCALAR }
        ]
    },
    'ISFORMULA': {
        method: 'isformula',
        parameters: [
            { argumentType: ArgumentTypes.NOERROR }
        ],
        doesNotNeedArgumentsToBeComputed: true
    },
    'ISNA': {
        method: 'isna',
        parameters: [
            { argumentType: ArgumentTypes.SCALAR }
        ]
    },
    'ISREF': {
        method: 'isref',
        parameters: [
            { argumentType: ArgumentTypes.SCALAR }
        ]
    },
    'ISERROR': {
        method: 'iserror',
        parameters: [
            { argumentType: ArgumentTypes.SCALAR }
        ]
    },
    'ISBLANK': {
        method: 'isblank',
        parameters: [
            { argumentType: ArgumentTypes.SCALAR }
        ]
    },
    'ISNUMBER': {
        method: 'isnumber',
        parameters: [
            { argumentType: ArgumentTypes.SCALAR }
        ]
    },
    'ISLOGICAL': {
        method: 'islogical',
        parameters: [
            { argumentType: ArgumentTypes.SCALAR }
        ]
    },
    'ISTEXT': {
        method: 'istext',
        parameters: [
            { argumentType: ArgumentTypes.SCALAR }
        ]
    },
    'ISNONTEXT': {
        method: 'isnontext',
        parameters: [
            { argumentType: ArgumentTypes.SCALAR }
        ]
    },
    'INDEX': {
        method: 'index',
        parameters: [
            { argumentType: ArgumentTypes.RANGE },
            { argumentType: ArgumentTypes.NUMBER },
            { argumentType: ArgumentTypes.NUMBER, defaultValue: 1 },
        ]
    },
    'NA': {
        method: 'na'
    },
    'ROW': {
        method: 'row',
        parameters: [
            { argumentType: ArgumentTypes.NOERROR, optional: true }
        ],
        isDependentOnSheetStructureChange: true,
        doesNotNeedArgumentsToBeComputed: true,
    },
    'ROWS': {
        method: 'rows',
        isDependentOnSheetStructureChange: true,
        doesNotNeedArgumentsToBeComputed: true,
    },
    'SHEET': {
        method: 'sheet',
        parameters: [
            { argumentType: ArgumentTypes.NOERROR }
        ],
        doesNotNeedArgumentsToBeComputed: true
    },
    'SHEETS': {
        method: 'sheets',
        parameters: [
            { argumentType: ArgumentTypes.NOERROR }
        ],
        doesNotNeedArgumentsToBeComputed: true
    }
};
